import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  ButtonGroup,
  Card,
  Col,
  Container,
  Dropdown,
  Form,
  InputGroup,
  Row,
  Stack,
} from "react-bootstrap";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import ExcelJS from "exceljs";
import autoTable from "jspdf-autotable";
import jsPDF from "jspdf";
import DateRangePicker from "../../../common/CustomDateRangePicker";
import { Filter } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/Store";

import {
  downloadZipFileForDictations,
  getEditorSimpleSearch,
} from "../../../services/EditorWorkFlowAPI";
import AppointmentActionsDropdown from "../../../common/AppointmentActionsDropdown";

import Snackbar from "../../../common/Snackbar";
import {
  CASE_STAGE_STATUS,
  DOCTOR_TODO_CODES,
  EDITOR_REPORT_STATUS,
  EDITOR_SUMMARY_CODES,
  QME_APPOINTMENT_STATUS,
  TRANSCRIPTION_REPORT_STATUS,
} from "../../../common/Constant";
import CustomPagination from "../../../common/Pagination";
import TableRowsPerPage from "../../../common/TableRowsPerPage";
import Loader from "../../../common/Loader";
import { EDITOR_HEADERS } from "../Editor/actions/constant";
import EditorDWCExtensionRequest from "../Editor/components/EditorDWCExtensionRequest";
import DoctorCommonDrawer from "./components/DoctorCommonDrawer";
import AssignToDoctor from "./components/AssignToDoctor";
import {
  DOCTOR_TODO_HEADERS,
  EXPORT_DOCTOR_TODO_HEADERS,
} from "./actions/constant";
import DoctorApproveRework from "./components/DoctorApproveRework";
import DoctorUploadRecordTabIndex from "./components/DoctorUploadRecords/DoctorUploadRecordTabIndex";
import DoctorAddNotes from "./components/DoctorAddNotes";
import DoctorDataTable from "./components/DoctorDataTable";
import {
  getDoctorGridSearch,
  getDoctorSummaryCard,
} from "../../../services/DoctorWorkflowAPI";
import useAnimatedCounter from "../useAnimatedCounter";
import UploadStatusFloatingIcon from "../../../common/UploadStatusFloatingIcon";
import { useLocation, useNavigate } from "react-router-dom";
import {
  setIsDashboard,
  setNewCaseInFoID,
} from "../../../redux/slice/CaseHeaderSlice";
import { getDownloadFileForPopUpDetails } from "../../../services/UploadRecordsApi";

interface EditorDashboardProps {
  setShowMainHeader: React.Dispatch<React.SetStateAction<boolean>>;
}

const DoctorDashboard: React.FC<EditorDashboardProps> = ({
  setShowMainHeader,
}) => {
  const loggedInUserid = useSelector((state: RootState) => state.auth.userid);
  const [doctorData, setDoctorData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const [activeFilter, setActiveFilter] = useState(
    DOCTOR_TODO_CODES.OVERDUE_TODAY,
  );
  const [activeCard, setActiveCard] = useState<string>("Overdue");
  const [activeSubCard, setActiveSubCard] = useState<string>("Today");
  const [activeListName, setActiveListName] = useState<string>(
    "Overdue List (Today)",
  );

  const [allReports, setAllReports] = useState<string>("");
  const [neardueCount, setNeardueCount] = useState<string>("");
  const [todayOverdueCount, setTodayOverdueCount] = useState<string>("");
  const [overdueCount, setOverdueCount] = useState<string>("");
  const [pendingDictation, setPendingDictation] = useState<string>("");
  const [pendingTranscription, setPendingTranscription] = useState<string>("");
  const [qmeReportsInProgressCount, setQMEReportsInProgressCount] =
    useState<string>("");
  const [qmeReportsCompletedCount, setQMEReportsCompletedCount] =
    useState<string>("");
  const [qmeSent, setQMESent] = useState<string>("");
  const [qmeApproved, setQMEApproved] = useState<string>("");
  const [qmeRework, setQMERework] = useState<string>("");

  const [dwcExtensionsCount, setDWEExtensionsCount] = useState<string>("");
  const [exceptionCount, setExceptionCount] = useState<string>("");

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [dwcExtensionId, setdwcExtensionId] = useState<string[]>([]);
  const [qmeScheduleId, setqmeScheduleId] = useState<Number>();
  const [selectAll, setSelectAll] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any[]>([]);
  const [showModalNew, setShowModalNew] = useState(false);

  const [totalRows, setTotalRows] = useState<any>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(15);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<string>("DESC");

  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 2, 0);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [error, setError] = useState("");
  const [downloadStatuses, setDownloadStatuses] = useState<any>({});

  const [dateRange, setDateRange] = useState<
    [Date | undefined, Date | undefined]
  >([firstDayOfMonth, lastDayOfMonth]);

  const [computedMinDate, setComputedMinDate] = useState<Date | undefined>();
  const [computedMaxDate, setComputedMaxDate] = useState<Date | undefined>();

  const handleDateRangeChange = (newRange: [Date | null, Date | null]) => {
    const [start, end] = newRange;
    setDateRange([start ?? undefined, end ?? undefined]);
  };

  useEffect(() => {
    // 📅 Allow selection from 1 year before today to 1 year after today
    const min = new Date(today);
    min.setFullYear(today.getFullYear() - 1);

    const max = new Date(today);
    max.setFullYear(today.getFullYear() + 1);

    setComputedMinDate(min);
    setComputedMaxDate(max);
  }, []);

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [dwcStatus, setdwcStatus] = useState<string>("");

  const inputRef = useRef<HTMLInputElement | null>(null);

  const toLocalISOString = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getSearchDoctorData = async (
    query: string = "",
    action: string = "",
    pageNumber: number = 1,
    pageSize: number = itemsPerPage,
    key: string = sortColumn,
    direction: string = sortOrder,
  ) => {
    try {
      setLoading(true);
      // Convert dateRange to ISO strings safely
      const trimmedQuery = query?.trim();
      const [start, end] = dateRange;
      const payload = {
        searchString: trimmedQuery || "",
        startDate: start ? toLocalISOString(start) : "",
        endDate: end ? toLocalISOString(end) : "",
        actionTrigger: activeFilter,
        // userId: 96,

        pageNumber,
        pageSize,
        sortColumn: key,
        sortOrder: direction,
      };

      const response = await getDoctorGridSearch(payload);

      // Step 3: Set state with updated response
      setDoctorData(response);
      setTotalPages(response[0]?.totalPages || 1);
      setTotalRows(response[0]?.totalCount || null);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const latestSummaryRequestId = useRef(0);

  const getDoctorSummaryData = async (
    query: string = "",
    action: string = "",
  ) => {
    const requestId = ++latestSummaryRequestId.current;
    try {
      // Convert dateRange to ISO strings safely
      const trimmedQuery = query?.trim();
      const [start, end] = dateRange;
      const payload = {
        searchString: query || "",
        startDate: start ? toLocalISOString(start) : "",
        endDate: end ? toLocalISOString(end) : "",

        // actionTrigger: action,
      };

      const response = await getDoctorSummaryCard(payload);
      if (requestId !== latestSummaryRequestId.current) return;

      if (response) {
        setAllReports(response?.totalAllReports ?? "");
        setTodayOverdueCount(response?.todayDueCount ?? "");
        setOverdueCount(response?.totalOverdueCount ?? "");
        setNeardueCount(response?.totalOverdueNearDue ?? "");
        setQMESent(response?.totalQMEAppSent ?? "");
        setQMEApproved(response?.totalQMEAppApproved ?? "");
        setQMERework(response?.totalQMEAppRework ?? "");
        setQMEReportsInProgressCount(response?.totalQMEAppInProgress ?? "");
        setQMEReportsCompletedCount(response?.totalQMEAppCompleted ?? "");
        setDWEExtensionsCount(response?.totalDWCExtensions ?? "");
        setExceptionCount(response?.totalExceptionCount ?? "");
        setPendingDictation(response?.totalPendingDictation ?? "");
        setPendingTranscription(response?.totalPendingTranscription ?? "");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const [start, end] = dateRange;

    if (start && end) {
      getSearchDoctorData(
        searchQuery,
        activeFilter,
        currentPage,
        itemsPerPage,
        sortColumn,
        sortOrder,
      );
    }
  }, [
    dateRange,
    searchQuery,
    currentPage,
    itemsPerPage,
    sortColumn,
    sortOrder,
  ]);

  useEffect(() => {
    const [start, end] = dateRange;

    if (start && end) {
      getDoctorSummaryData(searchQuery, activeFilter);
    }
  }, [dateRange, searchQuery]);

  const debounce = (func: Function, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  const handleInputChange = debounce((value: string) => {
    if (value.length >= 3 || value.length === 0) {
      setSearchQuery(value);
      setCurrentPage(1);
    }
  }, 300);

  const handleResetFilters = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }

    setSearchQuery("");
    // setTotalPages(1);
    // setCurrentPage(1);
    // setSortOrder("DESC");
    // setDateRange([firstDayOfMonth, lastDayOfMonth]);

    // const filter = DOCTOR_TODO_CODES.OVERDUE_NEARDUE;

    // setActiveCard("Overdue");
    // setActiveSubCard("Today");
    // setActiveFilter(filter);
  };

  useEffect(() => {
    if (!activeFilter) return;

    const filterQuery = searchQuery || "";
    // First get summary
    getDoctorSummaryData(filterQuery, activeFilter);

    // Then get search table
    getSearchDoctorData(
      filterQuery,
      activeFilter,
      1,
      itemsPerPage,
      sortColumn,
      "DESC",
    );
  }, [activeFilter, dateRange]);

  const assignDoctor = () => {
    setSnackbarMessage("Assigned to doctor successfully.");
    setError("success");
    setSnackbarOpen(true);
    handleSaveDoctorChanges();
  };

  const handleSaveDoctorChanges = () => {
    setSelectedIds([]);
    setSelectedRowData([]);
    setSelectAll(false);
    setShowMainHeader(true);
    setShowAppointmentActionDrawer(false);
    getSearchDoctorData(
      searchQuery,
      activeFilter,
      currentPage,
      itemsPerPage,
      sortColumn,
      sortOrder,
    );
    getDoctorSummaryData(searchQuery, activeFilter);
  };

  const NON_SELECTABLE_STAGES = [
    CASE_STAGE_STATUS.CLOSED,
    CASE_STAGE_STATUS.CANCELLED,
  ];

  const handleCheckboxSelect = (selectedRows: any[]) => {
    const invalidRows = selectedRows.filter((row) =>
      NON_SELECTABLE_STAGES.includes(row?.caseStage),
    );

    if (invalidRows.length > 0) {
      const hasClosed = invalidRows.some(
        (row) => row.caseStage === CASE_STAGE_STATUS.CLOSED,
      );

      const hasCancelled = invalidRows.some(
        (row) => row.caseStage === CASE_STAGE_STATUS.CANCELLED,
      );

      let message = "Invalid case selection.";

      if (hasClosed && hasCancelled) {
        message = "Some cases are closed and some are cancelled.";
      } else if (hasClosed) {
        message = "Case has been closed.";
      } else if (hasCancelled) {
        message = "Case has been cancelled.";
      }

      setSnackbarMessage(message);
      setError("error");
      setSnackbarOpen(true);
    }

    const validRows = selectedRows.filter(
      (row) => !NON_SELECTABLE_STAGES.includes(row?.caseStage),
    );

    const ids = validRows
      ?.map((r: any) => String(r?.subjectQmeScheduleId))
      ?.filter(Boolean);

    const infoId = validRows
      ?.map((r: any) => String(r?.dwcExtensionId))
      ?.filter(Boolean);

    const dwcStatus = validRows
      ?.map((r: any) => String(r?.dwcStatus))
      ?.filter(Boolean);

    setSelectedIds(ids);
    setdwcExtensionId(infoId);
    setqmeScheduleId(Number(ids));
    setSelectedRowData(validRows);
    setdwcStatus(String(dwcStatus));
  };

  const [selectedAction, setSelectedAction] = useState<string | number>("");

  const [showAppointmentActionDrawer, setShowAppointmentActionDrawer] =
    useState(false);
  const [drawerTitle, setDrawerTitle] = useState("");

  const allOptions = [
    {
      value: "Assign QME Doctor",
      label: "Assign To  QME Doctor",
    },
    {
      value: "Approve Rework",
      label: "Approve / Rework Report",
    },
    {
      value: "Upload Records",
      label: "Upload / Review Records",
    },
    {
      value: "Add Notes",
      label: "Add Notes",
    },
    {
      value: "DWC Extension",
      label: "DWC Extension",
    },
    {
      value: "Download Dictation",
      label: "Download Dictation",
    },
  ];

  const isDwcExtensionAllowed = (dueDate?: string) => {
    if (!dueDate) return false;

    // Format: MM/DD/YYYY
    const [month, day, year] = dueDate.split("/").map(Number);
    const due = new Date(year, month - 1, day);
    const today = new Date();

    today.setHours(0, 0, 0, 0);
    due.setHours(0, 0, 0, 0);

    const diffInDays =
      (due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24);

    return diffInDays <= 0 || diffInDays <= 5;
  };

  const multiSelectRestrictedValues = [
    "Approve Rework",
    "Upload Records",
    "Add Notes",
    "DWC Extension",
  ];

  const selectedRow = selectedRowData?.[0];

  const ASSIGN_EDITOR_BLOCKED_STATUSES = [
    EDITOR_REPORT_STATUS.SENT_FOR_REWORK,
    EDITOR_REPORT_STATUS.APPROVED_BY_DOCTOR,
    EDITOR_REPORT_STATUS.PENDING_DOCTOR_APPROVAL,
    EDITOR_REPORT_STATUS.COMPLETED,

    EDITOR_REPORT_STATUS.EDITING_PENDING,
  ];

  const hasBlockedAssignEditorStatus = selectedRowData?.some((row) =>
    ASSIGN_EDITOR_BLOCKED_STATUSES.includes(row?.reportStatus),
  );

  const isDictationAvailable = selectedRowData?.some(
    (row) => row.isDictationAvailable === 1,
  );

  const isEditingNotInitiated = selectedRowData?.some(
    (row) =>
      (row.reportStatus === EDITOR_REPORT_STATUS.EDITING_NOT_INITIATED ||
        row.reportStatus ===
        TRANSCRIPTION_REPORT_STATUS.TRANSCRIPTION_PENDING) &&
      row.appointmentStatus != QME_APPOINTMENT_STATUS.COMPLETED,
  );
  // const isTranscriptionPending = selectedRowData?.some(
  //   (row) =>
  //     (row.reportStatus === TRANSCRIPTION_REPORT_STATUS.DICTATION_PENDING
  //     ) &&
  //     row.appointmentStatus != QME_APPOINTMENT_STATUS.COMPLETED,
  // );

  const isEditingNotStarted = selectedRowData?.some(
    (row) =>
      (row.reportStatus === EDITOR_REPORT_STATUS.EDITING_PENDING ||
        row.reportStatus === EDITOR_REPORT_STATUS.EDITING_NOT_INITIATED ||
        row.reportStatus === TRANSCRIPTION_REPORT_STATUS.TRANSCRIPTION_PENDING ||
        row.reportStatus === TRANSCRIPTION_REPORT_STATUS.TRANSCRIPTION_IN_PROGRESS) &&
      row.isDictationAvailable === 1,
  );

  const isCompletedReport =
    selectedRow?.reportStatus === EDITOR_REPORT_STATUS.COMPLETED;

  const isEditorPendingReport =
    selectedRow?.reportStatus === EDITOR_REPORT_STATUS.EDITING_IN_PROGRESS ||
    selectedRow?.reportStatus === EDITOR_REPORT_STATUS.EDITING_PENDING;

  const isEditingPending =
    selectedRow?.reportStatus === EDITOR_REPORT_STATUS.APPROVED_BY_DOCTOR ||
    selectedRow?.reportStatus ===
    EDITOR_REPORT_STATUS.PENDING_DOCTOR_APPROVAL ||
    selectedRow?.reportStatus === EDITOR_REPORT_STATUS.SENT_FOR_REWORK ||
    selectedRow?.reportStatus === EDITOR_REPORT_STATUS.EXCEPTION;

  const isMultiSelect = selectedIds?.length > 1;
  const isPastOverdueList = activeListName === "Overdue List (Past Overdue)";

  const filteredOptions = allOptions.filter((opt) => {
    if (isMultiSelect && multiSelectRestrictedValues.includes(opt.value)) {
      return false;
    }

    if (opt.value === "Assign QME Doctor" && hasBlockedAssignEditorStatus) {
      return false;
    }

    // if (
    //   ["Assign QME Doctor", "Upload Records"].includes(opt.value) &&
    //   isEditingNotInitiated
    // ) {
    //   return false;
    // }
    // if (
    //   ["Assign QME Doctor", "Upload Records"].includes(opt.value) &&
    //   isTranscriptionPending
    // ) {
    //   return false;
    // }

    if (
      ["Assign QME Doctor", "Upload Records"].includes(opt.value) &&
      isEditingNotStarted
    ) {
      return false;
    }
    if (opt.value === "Approve Rework" && !isEditingPending) {
      return false;
    }

    if (opt.value === "DWC Extension" && isPastOverdueList) {
      return false;
    }

    if (opt.value === "DWC Extension") {
      return isDwcExtensionAllowed(selectedRow?.dueDate);
    }

    // if (opt.value === "Upload Records" && isEditorPendingReport) {
    //   return false;
    // }

    return true;
  });

  const finalOptions = filteredOptions.map((opt) => {
    if (
      opt.value === "Upload Records" &&
      (isCompletedReport || activeCard == "Pending List (Dictation)")
    ) {
      return {
        ...opt,
        label: "Review Records",
      };
    }

    return opt;
  });

  const handleActionChange = (value: string | number) => {
    setSelectedAction(value);
    if (value === "Assign QME Doctor") {
      setDrawerTitle("Assign QME Doctor");
      setShowAppointmentActionDrawer(true);
    }
    if (value === "Add Notes") {
      setDrawerTitle("Add Notes");
      setShowAppointmentActionDrawer(true);
    }

    if (value === "DWC Extension") {
      setDrawerTitle("DWC Extension");
      setShowAppointmentActionDrawer(true);
    }

    if (value === "Upload Records") {
      setShowMainHeader(false);
    }
    if (value === "Approve Rework") {
      setShowMainHeader(false);
    }
  };

  const refreshAppointmentData = () => {
    handleResetFilters();
    setSelectedIds([]);
    setSelectedRowData([]);
    setSelectAll(false);
    setShowMainHeader(true);
  };

  const handlecloseActionDrawer = async () => {
    handleSaveDoctorChanges();
    handleCloseModalNew();
  };

  const handleActionDrawer = () => {
    setShowAppointmentActionDrawer(false);
  };

  const handleCloseModalNew = () => {
    setShowModalNew(false);
    setSelectedAction("");
    // refreshAppointmentData();
  };

  const getBase64FromUrl = async (url: string): Promise<string> => {
    const res = await fetch(url);
    const blob = await res.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === "string") {
          resolve(reader.result);
        } else {
          reject("Failed to convert blob to base64");
        }
      };
      reader.onerror = () => reject("Failed to read blob");
      reader.readAsDataURL(blob);
    });
  };
  const handleExportPDF = async () => {
    try {
      const selectedStatus = activeListName;
      const [startDate, endDate] = dateRange;

      // ---------- Date Label ----------
      const formatDate = (date: Date) =>
        date.toLocaleDateString("en-US", {
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
        });

      let dateLabel = "";
      if (startDate && endDate && startDate.getTime() !== endDate.getTime()) {
        dateLabel = `${formatDate(startDate)} to ${formatDate(endDate)}`;
      } else {
        dateLabel = formatDate(startDate || new Date());
      }

      const title = `Appointments for ${selectedStatus} ${dateLabel}`;

      const logoBase64 = await getBase64FromUrl("/logo.png");

      // ---------- Headers ----------
      const headers: string[] = [
        "Sr. No",
        ...EXPORT_DOCTOR_TODO_HEADERS.map((h: any) => h.label || h.name),
      ];

      // ---------- API ----------
      const trimmedQuery = searchQuery?.trim();
      const payload = {
        searchString: trimmedQuery,
        startDate: startDate ? toLocalISOString(startDate) : "",
        endDate: endDate ? toLocalISOString(endDate) : "",
        actionTrigger: activeFilter,
        pageNumber: 1,
        pageSize: 100000,
        sortColumn: "",
        sortOrder: "DESC",
      };

      const response = await getDoctorGridSearch(payload);

      if (!Array.isArray(response) || response.length === 0) {
        alert("No data available for export");
        return;
      }

      // ---------- Table Data (IMPORTANT FIX) ----------
      const tableData: (string | number | null)[][] = response.map(
        (row: any, index: number) => {
          const obj: (string | number | null)[] = [];
          obj.push(index + 1);

          EXPORT_DOCTOR_TODO_HEADERS.forEach((header: any) => {
            let val = row[header.key] ?? "";

            if (header.key === "caseid" && typeof val === "string") {
              val = val.slice(-3);
            }

            obj.push(val);
          });

          return obj;
        },
      );

      // ---------- PDF ----------
      const doc = new jsPDF("landscape", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 10;

      autoTable(doc, {
        head: [headers],
        body: tableData,
        margin: { top: 28, left: 2 },
        styles: { fontSize: 9, cellPadding: 2, overflow: "linebreak" },
        tableWidth: pageWidth - margin,
        didDrawPage: (data) => {
          // ===== HEADER =====
          // ===== HEADER =====
          doc.setFontSize(12);
          doc.text("Medical Legal Portal", 2, 14);
          doc.addImage(logoBase64, "PNG", pageWidth - margin - 28, 5, 34, 12);

          // ===== TITLE (below header, same left alignment) =====
          doc.setFontSize(12);
          doc.text(title, 2, 22); // same X (2), lower Y

          // ===== FOOTER =====
          const footerY = pageHeight - 10;
          const now = new Date();
          const formattedDate = now.toLocaleString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          });

          const firstName = localStorage.getItem("firstname") ?? "";
          const lastName = localStorage.getItem("lastname") ?? "";

          const generatedByText = `Generated By & On - ${firstName} ${lastName} ${formattedDate}`;
          const pageNumberText = `Page No. ${data.pageNumber
            } of ${doc.getNumberOfPages()}`;

          doc.setDrawColor(0);
          doc.setLineWidth(0.1);
          doc.line(1, footerY - 5, pageWidth - 1, footerY - 5);

          doc.setFontSize(8);
          doc.text(generatedByText, 1, footerY);
          doc.text(
            pageNumberText,
            pageWidth - doc.getTextWidth(pageNumberText) - 1,
            footerY,
          );
        },
      });

      const safeFileName = title.replace(/[\/\\:*?"<>|]/g, "-");
      doc.save(`${safeFileName}.pdf`);
    } catch (error) {
      console.error("PDF Export Error:", error);
    }
  };

  const handleExportExcel = async () => {
    const sheetName = "DoctorData";
    const selectedStatus = activeListName;
    const [startDate, endDate] = dateRange;

    const formatDate = (date: Date) =>
      date.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      });

    let dateLabel = "";
    if (startDate && endDate && startDate.getTime() !== endDate.getTime()) {
      dateLabel = `${formatDate(startDate)} to ${formatDate(endDate)}`;
    } else {
      dateLabel = formatDate(startDate || new Date());
    }

    const title = `Appointments for ${selectedStatus} ${dateLabel}`;

    const headers = [
      "Sr. No",
      ...EXPORT_DOCTOR_TODO_HEADERS.map((h: any) => h.label || h.name),
    ];
    const trimmedQuery = searchQuery?.trim();
    const payload = {
      searchString: trimmedQuery,
      startDate: startDate ? toLocalISOString(startDate) : "",
      endDate: endDate ? toLocalISOString(endDate) : "",
      actionTrigger: activeFilter,
      pageNumber: 1,
      pageSize: 100000,
      sortColumn: "",
      sortOrder: "DESC",
    };

    // ✅ USE RESPONSE DIRECTLY
    const response = await getDoctorGridSearch(payload);
    type ExcelRow = (string | number | null | undefined)[];

    const exportData: ExcelRow[] = response.map((row: any, index: number) => {
      const rowArr: ExcelRow = [];
      rowArr.push(index + 1);

      EXPORT_DOCTOR_TODO_HEADERS.forEach((header: any) => {
        let val = row[header.key];
        if (header.key === "caseid" && typeof val === "string") {
          val = val.slice(-3);
        }
        rowArr.push(val);
      });

      return rowArr;
    });

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(sheetName);

    // Title row
    worksheet.mergeCells(1, 1, 1, headers.length);
    const titleCell = worksheet.getCell(1, 1);
    titleCell.value = title;
    titleCell.font = { bold: true, size: 14 };

    // Header row
    const headerRow = worksheet.addRow(headers);
    headerRow.font = { bold: true };

    // Data rows
    exportData.forEach((row) => worksheet.addRow(row));

    // Column widths
    worksheet.columns.forEach((col, index) => {
      col!.width = index === 0 ? 10 : 25;
    });

    const buffer = await workbook.xlsx.writeBuffer();

    saveAs(
      new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `${title.replaceAll("/", "-")}.xlsx`,
    );
  };

  const handleDownloadProgress = async (
    id: string,
    downloadFn: () => Promise<any>,
    fileName: string = "File.zip",
  ) => {
    setDownloadStatuses((prev: any) => ({
      ...prev,
      [id]: { fileName, progress: 0, isUploading: true },
    }));

    let progress = 0;
    const interval = setInterval(() => {
      progress += 10;
      if (progress >= 90) return;
      setDownloadStatuses((prev: any) => ({
        ...prev,
        [id]: { ...prev[id], progress },
      }));
    }, 300);

    try {
      await downloadFn();
      clearInterval(interval);
      setDownloadStatuses((prev: any) => ({
        ...prev,
        [id]: { ...prev[id], progress: 100, isUploading: false },
      }));
      setTimeout(() => {
        setDownloadStatuses((prev: any) => {
          const updated = { ...prev };
          delete updated[id];
          return updated;
        });
      }, 2000);
    } catch (error) {
      console.error("Download error:", error);
      clearInterval(interval);
      setDownloadStatuses((prev: any) => ({
        ...prev,
        [id]: { ...prev[id], isUploading: false },
      }));
    }
  };

  const handleDownload = () => {
    const subjectQmeScheduleIDs = selectedRowData
      ?.map((item) => item.subjectQmeScheduleId)
      .filter((id) => id != null)
      .join(",");

    setSnackbarMessage("Downloading Dictations");
    setError("success");
    setSnackbarOpen(true);

    handleDownloadProgress(`doc-${Date.now()}`, () =>
      downloadZipFileForDictations(subjectQmeScheduleIDs),
    );

    setShowModalNew(false);
    setSelectedAction("");
    handleSaveDoctorChanges();
  };

  const handleSaveEditorDWCChanges = (statusName: string) => {
    setSelectedIds([]);
    setSelectedRowData([]);
    setSelectAll(false);
    setShowMainHeader(true);
    getSearchDoctorData(
      searchQuery,
      activeFilter,
      currentPage,
      itemsPerPage,
      sortColumn,
      sortOrder,
    );
    if (statusName != "Requested") {
      setShowAppointmentActionDrawer(false);
    }
    getDoctorSummaryData(searchQuery, activeFilter);
  };
  useEffect(() => {
    if (selectedAction === "Download Dictation") {
      const hasNoDictation = selectedRowData?.some(
        (item) => item.isDictationAvailable === 0,
      );

      if (hasNoDictation) {
        setSnackbarMessage("No dictation uploaded");
        setError("error");
        setSnackbarOpen(true);
        setSelectedAction("");
        setSelectedIds([]);
        setSelectedRowData([]);
        setSelectAll(false);
        return;
      }

      handleDownload();
    }
  }, [selectedAction]);

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    getSearchDoctorData(
      searchQuery,
      activeFilter,
      pageNumber,
      itemsPerPage,
      sortColumn,
      sortOrder,
    );
  };

  const handleSortAction = (row: any) => {
    setSortColumn(row.key);
    setSortOrder(row.direction);
    getSearchDoctorData(
      searchQuery,
      activeFilter,
      currentPage,
      itemsPerPage,
      row.key,
      row.direction,
    );
  };

  const handleRowsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  // Calculate text for the range of entries currently visible
  const entriesText = !totalRows
    ? "Showing 0 of 0 entries"
    : `Showing ${(currentPage - 1) * itemsPerPage + 1} to ${Math.min(
      currentPage * itemsPerPage,
      totalRows,
    )} of ${totalRows} entries`;

  const handleCardClick = (filter: string) => {
    const current = 1;
    const perPage = 15;
    const column = "";
    const order = "DESC";

    setActiveFilter(filter);
    setCurrentPage(current);
    setItemsPerPage(perPage);
    setSortColumn(column);
    setSortOrder(order);
    setSelectedIds([]);
    setSelectedRowData([]);
    setSelectAll(false);
  };
 let filteredHeaders = DOCTOR_TODO_HEADERS;

if (activeFilter === DOCTOR_TODO_CODES.TOTAL_PENDING_DICTATION) {
  filteredHeaders = DOCTOR_TODO_HEADERS.filter(
    (header) => header.label !== "Dictations"
  );
}
  const getCurrentMonthRange = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 2, 0);
    return [firstDay, lastDay] as [Date, Date];
  };

  const handleResetDate = () => {
    const [start, end] = getCurrentMonthRange();
    setDateRange([start, end]);
  };

  const animatedAllReports = useAnimatedCounter(allReports);
  const animatedNearDue = useAnimatedCounter(neardueCount);
  const animatedTodayOverdue = useAnimatedCounter(todayOverdueCount);
  const animatedOverdue = useAnimatedCounter(overdueCount);

  const animatedPendingDictation = useAnimatedCounter(pendingDictation);
  const animatedPendingTranscription = useAnimatedCounter(pendingTranscription);

  const animatedQMEReportsInProgress = useAnimatedCounter(
    qmeReportsInProgressCount,
  );
  const animatedQMEReportsCompleted = useAnimatedCounter(
    qmeReportsCompletedCount,
  );

  const animatedQMESent = useAnimatedCounter(qmeSent);
  const animatedQMEApproved = useAnimatedCounter(qmeApproved);
  const animatedQMERework = useAnimatedCounter(qmeRework);

  const animatedDWCExtensions = useAnimatedCounter(dwcExtensionsCount);
  const animatedExceptionCount = useAnimatedCounter(exceptionCount);

  const dispatch = useDispatch();

  const [visibleHeaderIndex, setVisibleHeaderIndex] = useState<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();

  const handleCaseClick = (row: any) => {
    dispatch(setIsDashboard(true));
    dispatch(setNewCaseInFoID(row.caseInfoId));
    setVisibleHeaderIndex(true);
    sessionStorage.setItem("fromMyTodos", "true");
    navigate("/search-case", {
      state: {
        from: "mytodos",
        myTodosState: {
          searchQuery,
          dateRange,
          activeFilter,
          activeCard,
          activeSubCard,
          activeListName,
          currentPage,
          itemsPerPage,
          sortColumn,
          sortOrder,
        },
      },
    });
  };

  const resetToDefault = () => {
    setSearchQuery("");
    setDateRange([firstDayOfMonth, lastDayOfMonth]);
    setActiveFilter(EDITOR_SUMMARY_CODES.OVERDUE_TODAY);
    setActiveCard("Overdue");
    setActiveSubCard("Today");
    setActiveListName("Overdue List (Today)");
    setCurrentPage(1);
    setItemsPerPage(15);
    setSortColumn("");
    setSortOrder("DESC");
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const hasInitialized = useRef(false);

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const cameFromMyTodos = sessionStorage.getItem("fromMyTodos");

    if (cameFromMyTodos === "true" && location.state?.from === "mytodos") {
      const s = location.state.myTodosState;

      setSearchQuery(s.searchQuery);
      setDateRange(s.dateRange);
      setActiveFilter(s.activeFilter);
      setActiveCard(s.activeCard);
      setActiveSubCard(s.activeSubCard);
      setActiveListName(s.activeListName);
      setCurrentPage(s.currentPage);
      setItemsPerPage(s.itemsPerPage);
      setSortColumn(s.sortColumn);
      setSortOrder(s.sortOrder);

      if (inputRef.current) {
        inputRef.current.value = s.searchQuery || "";
        getDoctorSummaryData(s.searchQuery, activeFilter);
      }

      sessionStorage.removeItem("fromMyTodos");
    } else {
      resetToDefault();
    }
  }, []);

  const handleDownloadSingleFileTranscriber = (row: any) => {
    handleDownloadProgress(
      row?.caseRecordDetailsId,
      () =>
        getDownloadFileForPopUpDetails(
          row?.caseRecordDetailsId,
          row?.transcriptionFileName,
        ),
      row?.transcriptionFileName,
    );
  };

  return (
    <>
      <UploadStatusFloatingIcon
        uploadStatuses={downloadStatuses}
        isUpload={false}
        onCancelUpload={(id: any) => {
          setDownloadStatuses((prev: any) => {
            const updated = { ...prev };
            delete updated[id];
            return updated;
          });
        }}
      />
      <Snackbar
        message={snackbarMessage}
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        severity={error}
      />
      {selectedAction === "Upload Records" ? (
        <DoctorUploadRecordTabIndex
          editorData={selectedRowData}
          selectedRowsData={selectedIds}
          close={handlecloseActionDrawer}
        />
      ) : selectedAction === "Approve Rework" ? (
        <DoctorApproveRework
          doctorData={selectedRowData}
          selectedRowsData={selectedIds}
          onClose={handlecloseActionDrawer}
        />
      ) : (
        <div className="main_wrapper_section appoinment_wrapper mt-3">
          <Loader isLoading={loading} />

          <Container fluid className="px-5">
            <Row className="g-3">
              {" "}
              {/* g-3 adds equal gaps between cards */}
              {/* Card 1 */}
              <Col md={2}>
                <Card
                  className={`h-100 todocard1 ${activeCard == "Overdue" ? "active" : ""
                    }`}
                  style={{
                    backgroundColor: "#ffeae9",
                    borderRadius: "5px",
                    border: "1px solid #FEA1A1",
                  }}
                  onClick={() => {
                    setActiveCard("Overdue");
                    setActiveListName("Overdue List (Today)");
                    handleCardClick(DOCTOR_TODO_CODES.OVERDUE_TODAY);
                    setActiveSubCard("Today");
                  }}
                >
                  <Card.Body className="d-flex flex-column justify-content-between p-1">
                    {/* Header Row */}
                    <div className="d-flex justify-content-between align-items-center ">
                      <Card.Title
                        style={{
                          color: "#FF6D6D",
                          fontSize: "14px",
                          fontWeight: "600",
                          marginBottom: "0px",
                        }}
                      >
                        Overdue Reports
                      </Card.Title>
                      <span
                        className="material-symbols-outlined"
                        style={{ color: "#fe6767" }}
                      >
                        timer
                      </span>
                    </div>

                    {/* Two-line details */}
                    <div className="d-flex justify-content-start">
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCardClick(DOCTOR_TODO_CODES.OVERDUE_NEARDUE);
                          setActiveSubCard("Near");
                          setActiveListName("Overdue List (Near Due)");
                          setActiveCard("Overdue");
                        }}
                        style={{
                          cursor: "pointer",
                          borderRight: "1px solid transparent",
                          background:
                            "linear-gradient(to bottom, transparent 10%, #E5B7B7 10%, #E5B7B7 90%, transparent 90%) right / 1px 100% no-repeat",
                        }}
                        className={`borderRight px-1 sub-section-1 ${activeSubCard == "Near" ? "active" : ""
                          }`}
                      >
                        <div style={{ fontSize: "11px", color: "#000" }}>
                          Near Due
                        </div>
                        <div
                          style={{
                            fontSize: "15px",
                            color: "#fe6767",
                            fontWeight: "600",
                          }}
                        >
                          {animatedNearDue}
                        </div>
                      </div>
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCardClick(DOCTOR_TODO_CODES.OVERDUE_TODAY);
                          setActiveSubCard("Today");
                          setActiveListName("Overdue List (Today)");
                          setActiveCard("Overdue");
                        }}
                        style={{
                          cursor: "pointer",
                          borderRight: "1px solid transparent",
                          background:
                            "linear-gradient(to bottom, transparent 10%, #E5B7B7 10%, #E5B7B7 90%, transparent 90%) right / 1px 100% no-repeat",
                        }}
                        className={`borderRight px-2 sub-section-1 ${activeSubCard == "Today" ? "active" : ""
                          }`}
                      >
                        <div style={{ fontSize: "11px", color: "#000" }}>
                          Today
                        </div>
                        <div
                          style={{
                            fontSize: "15px",
                            color: "#fe6767",
                            fontWeight: "600",
                          }}
                        >
                          {animatedTodayOverdue}
                        </div>
                      </div>
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCardClick(DOCTOR_TODO_CODES.OVERDUE);
                          setActiveSubCard("OVDU");
                          setActiveListName("Overdue List (Past Overdue)");
                          setActiveCard("Overdue");
                        }}
                        style={{
                          cursor: "pointer",
                        }}
                        className={`px-1 sub-section-1 ${activeSubCard == "OVDU" ? "active" : ""
                          }`}
                      >
                        <div style={{ fontSize: "11px", color: "#000" }}>
                          Overdue
                        </div>
                        <div
                          style={{
                            fontSize: "15px",
                            color: "#fe6767",
                            fontWeight: "600",
                          }}
                        >
                          {animatedOverdue}
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              {/* Card 2 */}
              <Col md={2}>
                <Card
                  className={`h-100 todocard2 ${activeCard == "Pending" ? "active" : ""
                    }`}
                  style={{
                    backgroundColor: "#FCECBE",
                    borderRadius: "5px",
                    cursor: "pointer",
                    border: "1px solid #E6C872",
                  }}
                  onClick={() => {
                    setActiveCard("Pending");
                    setActiveSubCard("PND");
                    setActiveListName("Pending List (Dictation)");
                    handleCardClick(DOCTOR_TODO_CODES.TOTAL_PENDING_DICTATION);
                  }}
                >
                  <Card.Body className="d-flex flex-column justify-content-between p-1">
                    <div className="d-flex justify-content-between align-items-center ">
                      <Card.Title
                        style={{
                          color: "#9C6F16",
                          fontSize: "14px",
                          fontWeight: "600",
                        }}
                      >
                        Pending
                      </Card.Title>
                      <span
                        className="material-symbols-outlined"
                        style={{ color: "#9b6d15" }}
                      >
                        hourglass_bottom
                      </span>
                    </div>
                    <div className="d-flex justify-content-start">
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveCard("Pending");

                          setActiveListName("Pending List (Dictation)");
                          handleCardClick(
                            DOCTOR_TODO_CODES.TOTAL_PENDING_DICTATION,
                          );
                          setActiveSubCard("PND");
                        }}
                        style={{
                          cursor: "pointer",
                          borderRight: "1px solid transparent",
                          padding: "0px 60px 0px 0px",
                          background:
                            "linear-gradient(to bottom, transparent 10%, #9b6d15 10%, #9b6d15 90%, transparent 90%) right / 1px 100% no-repeat",
                        }}
                        className={`borderRight px-1 sub-section-1 ${activeSubCard == "PND" ? "active" : ""
                          }`}
                      >
                        <div style={{ fontSize: "11px", color: "#9b6d15" }}>
                          Dictation
                        </div>
                        <div
                          style={{
                            fontSize: "15px",
                            color: "#9b6d15",
                            fontWeight: "600",
                          }}
                        >
                          {animatedPendingDictation}
                        </div>
                      </div>
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveCard("Pending");
                          setActiveSubCard("");
                          setActiveListName("Pending List (Transcription)");
                          handleCardClick(
                            DOCTOR_TODO_CODES.PENDING_TRANSCRIPTION,
                          );
                          setActiveSubCard("PNT");
                        }}
                        style={{
                          cursor: "pointer",
                          padding: "0px 10px 0px 10px",
                        }}
                        className={` px-1 sub-section-1 ${activeSubCard == "PNT" ? "active" : ""
                          }`}
                      >
                        <div style={{ fontSize: "11px", color: "#9b6d15" }}>
                          Transcription
                        </div>
                        <div
                          style={{
                            fontSize: "15px",
                            color: "#9b6d15",
                            fontWeight: "600",
                          }}
                        >
                          {animatedPendingTranscription}
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={2}>
                <Card
                  className={`h-100 todocard4 ${activeCard === "QME Approval" ? "active" : ""
                    }`}
                  style={{
                    backgroundColor: "#E9E5FE",
                    borderRadius: "5px",
                    border: "1px solid #9C8CEE",
                  }}
                  onClick={() => {
                    setActiveCard("QME Approval");
                    setActiveListName("QME Approval List (Received)");
                    handleCardClick(DOCTOR_TODO_CODES.QME_SENT);
                    setActiveSubCard("QSE");
                  }}
                >
                  <Card.Body className="d-flex flex-column justify-content-between p-1">
                    {/* Header Row */}
                    <div className="d-flex justify-content-between align-items-center ">
                      <Card.Title
                        style={{
                          color: "#684fe5",
                          fontSize: "14px",
                          fontWeight: "600",
                        }}
                      >
                        QME Approval
                      </Card.Title>
                      <span
                        className="material-symbols-outlined"
                        style={{ color: "#684fe5" }}
                      >
                        editor_choice
                      </span>
                    </div>

                    {/* Two-line details */}
                    <div className="d-flex justify-content-start">
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveListName("QME Approval List (Received)");
                          handleCardClick(DOCTOR_TODO_CODES.QME_SENT);
                          setActiveCard("QME Approval");
                          setActiveSubCard("QSE");
                        }}
                        style={{
                          cursor: "pointer",
                          borderRight: "1px solid transparent",
                          background:
                            "linear-gradient(to bottom, transparent 10%, #C3BAF2 10%, #C3BAF2 90%, transparent 90%) right / 1px 100% no-repeat",
                        }}
                        className={`borderRight px-1 sub-section-1 ${activeSubCard == "QSE" ? "active" : ""
                          }`}
                      >
                        <div style={{ fontSize: "11px", color: "#684fe5" }}>
                          Received
                        </div>
                        <div
                          style={{
                            fontSize: "15px",
                            color: "#684fe5",
                            fontWeight: "600",
                          }}
                        >
                          {animatedQMESent}
                        </div>
                      </div>
                      {/* <div
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveListName("QME Approval List (Approved)");
                          handleCardClick(DOCTOR_TODO_CODES.QME_APPROVED);
                          setActiveCard("QME Approval");
                          setActiveSubCard("QAE");
                        }}
                        style={{
                          cursor: "pointer",
                          borderRight: "1px solid transparent",
                          background:
                            "linear-gradient(to bottom, transparent 10%, #C3BAF2 10%, #C3BAF2 90%, transparent 90%) right / 1px 100% no-repeat",
                        }}
                        className={`borderRight px-1 sub-section-1 ${
                          activeSubCard == "QAE" ? "active" : ""
                        }`}
                      >
                        <div style={{ fontSize: "11px", color: "#684fe5" }}>
                          Approved
                        </div>
                        <div
                          style={{
                            fontSize: "15px",
                            color: "#684fe5",
                            fontWeight: "600",
                          }}
                        >
                          {animatedQMEApproved}
                        </div>
                      </div> */}
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveListName("QME Approval List (Rework)");
                          handleCardClick(DOCTOR_TODO_CODES.QME_REWORK);
                          setActiveSubCard("QRE");
                          setActiveCard("QME Approval");
                        }}
                        style={{
                          cursor: "pointer",
                        }}
                        className={`px-1 sub-section-1 ${activeSubCard == "QRE" ? "active" : ""
                          }`}
                      >
                        <div style={{ fontSize: "11px", color: "#684fe5" }}>
                          Rework
                        </div>
                        <div
                          style={{
                            fontSize: "15px",
                            color: "#684fe5",
                            fontWeight: "600",
                          }}
                        >
                          {animatedQMERework}
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={2}>
                <Card
                  className={`h-100 todocard7 ${activeCard === "QME Reports" ? "active" : ""
                    }`}
                  style={{
                    backgroundColor: "#ccffd5",
                    borderRadius: "5px",
                    border: "1px solid #2f9d50",
                  }}
                  onClick={() => {
                    setActiveCard("QME Reports");
                    setActiveListName("QME Reports List (Ongoing)");
                    handleCardClick(DOCTOR_TODO_CODES.QME_IN_PROGRESS);
                    setActiveSubCard("QIE");
                  }}
                >
                  <Card.Body className="d-flex flex-column justify-content-between p-1">
                    {/* Header Row */}
                    <div className="d-flex justify-content-between align-items-center ">
                      <Card.Title
                        style={{
                          color: "#2f9d50",
                          fontSize: "14px",
                          fontWeight: "600",
                        }}
                      >
                        QME Reports
                      </Card.Title>
                      <span
                        className="material-symbols-outlined"
                        style={{ color: "#2f9d50" }}
                      >
                        assignment
                      </span>
                    </div>

                    {/* Two-line details */}
                    <div className="d-flex justify-content-start">
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCardClick(DOCTOR_TODO_CODES.QME_IN_PROGRESS);
                          setActiveListName("QME Reports List (Ongoing)");
                          setActiveCard("QME Reports");
                          setActiveSubCard("QIE");
                        }}
                        style={{
                          cursor: "pointer",
                          borderRight: "1px solid transparent",
                          padding: "0px 10px 0px 0px",
                          background:
                            "linear-gradient(to bottom, transparent 10%, #B6DDEF 10%, #B6DDEF 90%, transparent 90%) right / 1px 100% no-repeat",
                        }}
                        className={`borderRight px-1 sub-section-1 ${activeSubCard == "QIE" ? "active" : ""
                          }`}
                      >
                        <div style={{ fontSize: "11px", color: "#2f9d50" }}>
                          Ongoing
                        </div>
                        <div
                          style={{
                            fontSize: "15px",
                            color: "#2f9d50",
                            fontWeight: "600",
                          }}
                        >
                          {animatedQMEReportsInProgress}
                        </div>
                      </div>
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveListName("QME Reports List (Completed)");
                          handleCardClick(DOCTOR_TODO_CODES.QME_COMPLETED);
                          setActiveCard("QME Reports");
                          setActiveSubCard("QCE");
                        }}
                        style={{
                          cursor: "pointer",
                          borderRight: "1px solid transparent",
                          padding: "0px 10px 0px 0px",
                          background:
                            "linear-gradient(to bottom, transparent 10%, #B6DDEF 10%, #B6DDEF 90%, transparent 90%) right / 1px 100% no-repeat",
                        }}
                        className={`borderRight px-1 sub-section-1 ${activeSubCard == "QCE" ? "active" : ""
                          }`}
                      >
                        <div style={{ fontSize: "11px", color: "#2f9d50" }}>
                          Completed
                        </div>
                        <div
                          style={{
                            fontSize: "15px",
                            color: "#2f9d50",
                            fontWeight: "600",
                          }}
                        >
                          {animatedQMEReportsCompleted}
                        </div>
                      </div>
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveListName("QME Reports List (Extensions)");
                          handleCardClick(DOCTOR_TODO_CODES.DWC_EXTENSION);
                          setActiveCard("QME Reports");
                          setActiveSubCard("QDWC");
                        }}
                        style={{
                          cursor: "pointer",
                          padding: "0px 10px 0px 10px",
                        }}
                        className={` px-1 sub-section-1 ${activeSubCard == "QDWC" ? "active" : ""
                          }`}
                      >
                        <div style={{ fontSize: "11px", color: "#2f9d50" }}>
                          Extensions
                        </div>
                        <div
                          style={{
                            fontSize: "15px",
                            color: "#2f9d50",
                            fontWeight: "600",
                          }}
                        >
                          {animatedDWCExtensions}
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              {/* Add more cards similarly */}
              <Col md={2}>
                <Card
                  className={`h-100 todocard9 ${activeCard == "Exception" ? "active" : ""
                    }`}
                  style={{
                    backgroundColor: "#f8dcb4",
                    borderRadius: "5px",
                    border: "1px solid #EFCA93",
                  }}
                  onClick={() => {
                    setActiveCard("Exception");
                    setActiveListName("Exception List");
                    setActiveSubCard("");
                    handleCardClick(DOCTOR_TODO_CODES.EXCEPTION);
                  }}
                >
                  <Card.Body className="d-flex flex-column justify-content-between p-1">
                    <div className="d-flex justify-content-between align-items-center ">
                      <Card.Title
                        style={{
                          color: "#995911",
                          fontSize: "14px",
                          fontWeight: "600",
                        }}
                      >
                        Exception
                      </Card.Title>
                      <span
                        className="material-symbols-outlined"
                        style={{ color: "#995911" }}
                      >
                        question_mark
                      </span>
                    </div>
                    <div
                      style={{
                        color: "#995911",
                        fontSize: "1.1rem",
                        fontWeight: "600",
                      }}
                    >
                      {animatedExceptionCount}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={2}>
                <Card
                  className={`h-100 todocard5 ${activeCard === "All Reports" ? "active" : ""
                    }`}
                  style={{
                    backgroundColor: "#e4eff1",
                    borderRadius: "5px",
                    cursor: "pointer",
                    border: "1px solid #33919f",
                  }}
                  onClick={() => {
                    setActiveCard("All Reports");
                    setActiveListName("All Reports List");
                    setActiveSubCard("");
                    handleCardClick(DOCTOR_TODO_CODES.ALL_REPORTS);
                  }}
                >
                  <Card.Body className="d-flex flex-column justify-content-between p-1">
                    <div className="d-flex justify-content-between align-items-center ">
                      <Card.Title
                        style={{
                          color: "#33919f",
                          fontSize: "14px",
                          fontWeight: "600",
                        }}
                      >
                        All Reports
                      </Card.Title>
                      <span
                        className="material-symbols-outlined"
                        style={{ color: "#33919f" }}
                      >
                        timer
                      </span>
                    </div>
                    <div
                      style={{
                        color: "#33919f",
                        fontSize: "1.1rem",
                        fontWeight: "600",
                      }}
                    >
                      {animatedAllReports}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            <div
              className="my-3"
              style={{ background: "#0092D5", height: "2px" }}
            ></div>
            <div className="container-fluid px-2">
              <div className="mt-3 mb-3">
                <Row className="align-items-center justify-content-between mb-3">
                  {/* Left side: Title */}
                  <Col xs="auto" className="sectionTitle noborder pe-3">
                    {activeListName}
                  </Col>

                  {/* Right side: Controls */}
                  <Col xs="auto">
                    <div className="d-flex align-items-center gap-2">
                      {/* Date Picker */}
                      <div
                        className="date-picker-container appointment-data-range customDrodownControlDAtepicker"
                        style={{ width: "280px" }}
                      >
                        <DateRangePicker
                          label=""
                          onChange={handleDateRangeChange}
                          startDate={dateRange[0]}
                          endDate={dateRange[1]}
                          minDate={computedMinDate}
                          maxDate={computedMaxDate}
                          showResetIcon={!!dateRange[0] && !!dateRange[1]}
                          onReset={handleResetDate}
                        />
                      </div>

                      {/* Search Box */}
                      <InputGroup className="w-auto">
                        <InputGroup.Text className="px-2">
                          <span className="material-symbols-outlined text-black-50">
                            search
                          </span>
                        </InputGroup.Text>
                        <Form.Control
                          ref={inputRef}
                          aria-label="Search"
                          onChange={(e) =>
                            handleInputChange(e.target.value.trim())
                          }
                          onPaste={(e) => {
                            e.preventDefault();
                            const text = e.clipboardData.getData("text").trim();
                            handleInputChange(text);
                            if (inputRef.current) inputRef.current.value = text;
                          }}
                        />
                      </InputGroup>

                      {/* Reset Button */}
                      <Button
                        className="d-flex align-items-center"
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: "10px",
                          fontSize: "22px",
                        }}
                        onClick={handleResetFilters}
                      >
                        <span className="material-icons reset_ic0">
                          restart_alt
                        </span>
                      </Button>

                      {/* Export Dropdown */}
                      <Dropdown as={ButtonGroup}>
                        <Dropdown.Toggle
                          variant="outline"
                          className="d-flex align-items-center btn-text-border appoinmentdropdown p-2"
                        >
                          <span className="material-symbols-outlined">
                            download
                          </span>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={handleExportExcel}>
                            Export to Excel
                          </Dropdown.Item>
                          <Dropdown.Item onClick={handleExportPDF}>
                            Export to PDF
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>

                      {/* Actions Dropdown */}
                      {selectedIds?.length > 0 && (
                        <AppointmentActionsDropdown
                          name="Actions"
                          options={finalOptions}
                          value={selectedAction}
                          onChange={handleActionChange}
                        />
                      )}
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
            <div className="mb-2 container-fluid">
              <DoctorDataTable
                headers={filteredHeaders}
                rows={doctorData}
                isCheckbox={true}
                onSortAction={handleSortAction}
                selectedRowIds={selectedIds}
                onCheckboxSelect={handleCheckboxSelect}
                selectAll={selectAll}
                setSelectAll={setSelectAll}
                onSaveSuccess={assignDoctor}
                onCaseClick={handleCaseClick}
                activeListName={activeListName}
                onAction={handleDownloadSingleFileTranscriber}
              />
            </div>
            <div className="d-flex justify-content-between align-items-center mt-0 mb-4 row p-0 m-0">
              {/* Display entries text on the left */}
              <div className="col-md-12">
                <div className="row">
                  <div className="col-md-6 showingData_wrapper">
                    <span>{entriesText}</span>
                  </div>
                  <div className="col-md-6">
                    <div className="d-flex flex-row-reverse ">
                      <CustomPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={paginate}
                      />
                      <TableRowsPerPage
                        value={itemsPerPage}
                        onSelect={handleRowsPerPageChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Container>
          <DoctorCommonDrawer
            title={drawerTitle}
            open={showAppointmentActionDrawer}
            onClose={handleActionDrawer}
            placement="end"
            width={selectedAction === "Assign QME Doctor" ? "40%" : "60%"}
            height={selectedAction === "Assign QME Doctor" ? "400px" : "auto"}
            top={selectedAction === "Assign QME Doctor" ? "auto" : "175px"}
          >
            {selectedAction === "Add Notes" && (
              <DoctorAddNotes
                showDrawer={showAppointmentActionDrawer}
                onClose={handlecloseActionDrawer}
                caseInfoId={selectedRowData[0]?.caseInfoId}
                caseId={selectedRowData[0]?.caseId}
                isRowActionTriggered={false}
                onNotesSaved={() => { }}
                subjectQmeScheduleID={selectedRowData[0]?.subjectQmeScheduleId}
                 details = {selectedRowData[0]?.patientName + " - " + selectedRowData[0]?.caseOrExamType}
              />
            )}
            {selectedAction === "DWC Extension" && (
              <EditorDWCExtensionRequest
                selectedRowIds={selectedIds}
                selectedRows={selectedRowData}
                dwcExtensionIds={dwcExtensionId}
                subjectQmeScheduleID={qmeScheduleId}
                onClose={handlecloseActionDrawer}
                onSaveSuccess={handleSaveEditorDWCChanges}
                dwcStatus={dwcStatus}
                caseInfoId={selectedRowData[0]?.caseInfoId}
              />
            )}
            {selectedAction === "Assign QME Doctor" && (
              <AssignToDoctor
                doctorData={selectedRowData}
                SelectedRowsDataTo={selectedIds}
                showModalNew={showAppointmentActionDrawer}
                closeModel={handlecloseActionDrawer}
                onSaveSuccess={handleSaveDoctorChanges}
              />
            )}
          </DoctorCommonDrawer>
        </div>
      )}
    </>
  );
};

export default DoctorDashboard;
